// src/services/shopAdminService.js
// =============================================================================
// Internal frontend API wrapper for Web Shop order support views
// =============================================================================
//
// The public shop service stays focused on customer checkout. This service is
// for authenticated internal users and calls /api/shop/admin/... endpoints.
// It is intentionally read-heavy: orders are created by the web shop flow, not
// manually from the internal app.

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.message ||
    "Request failed"
  );
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);
  e.status = err?.response?.status;
  e.code = err?.response?.data?.code || err?.code;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[ShopAdminService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

function assertOrderId(context, orderId) {
  if (orderId === undefined || orderId === null || `${orderId}`.trim() === "") {
    const e = new Error(`${context}: orderId is required.`);
    e.code = "SHOP_ORDER_ID_MISSING";
    throw e;
  }
}

export const listShopOrders = async (params = {}) => {
  try {
    const res = await apiClient.get("/api/shop/admin/orders", {
      withCredentials: true,
      params,
    });

    const data = res?.data || {};
    return {
      rows: Array.isArray(data.rows) ? data.rows : [],
      count: Number(data.count || 0),
      limit: Number(data.limit || params.limit || 50),
      offset: Number(data.offset || params.offset || 0),
      amountTotal: Number(data.amountTotal || 0),
      pageAmountTotal: Number(data.pageAmountTotal || 0),
      currency: data.currency || "PLN",
    };
  } catch (err) {
    throwApiError("listShopOrders", err);
  }
};

export const resendShopVoucherEmail = async (orderId, payload = {}) => {
  try {
    assertOrderId("resendShopVoucherEmail", orderId);
    const res = await apiClient.post(
      `/api/shop/admin/orders/${encodeURIComponent(orderId)}/voucher-email/resend`,
      payload,
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );
    return res?.data;
  } catch (err) {
    throwApiError("resendShopVoucherEmail", err);
  }
};

export default {
  listShopOrders,
  resendShopVoucherEmail,
};
